import { ChevronDown, ChevronRight, Filter, X } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { useState, useMemo } from "react";
import { cn } from "@/lib/utils";

const FilterSection = ({ 
  title, 
  options, 
  selected, 
  onChange, 
  counts,
  defaultOpen = true 
}) => {
  const [open, setOpen] = useState(defaultOpen);

  const handleToggle = (option) => {
    if (selected.includes(option)) {
      onChange(selected.filter(x => x !== option));
    } else {
      onChange([...selected, option]);
    }
  };

  return (
    <Collapsible open={open} onOpenChange={setOpen} className="sidebar-section">
      <CollapsibleTrigger className="flex items-center justify-between w-full py-2 text-left">
        <span className="text-sm font-semibold text-slate-700 font-['Manrope']">{title}</span>
        {open ? (
          <ChevronDown className="w-4 h-4 text-slate-400" />
        ) : (
          <ChevronRight className="w-4 h-4 text-slate-400" />
        )}
      </CollapsibleTrigger>
      <CollapsibleContent className="pt-2 space-y-2">
        {options.map((option) => (
          <label
            key={option}
            className="flex items-center gap-3 py-1.5 px-2 rounded-lg hover:bg-slate-50 cursor-pointer transition-colors"
          >
            <Checkbox
              checked={selected.includes(option)}
              onCheckedChange={() => handleToggle(option)}
              className="border-slate-300 data-[state=checked]:bg-[#FF6F40] data-[state=checked]:border-[#FF6F40]"
              data-testid={`filter-${title.toLowerCase()}-${option.toLowerCase().replace(/\s+/g, '-')}`}
            />
            <span className="text-sm text-slate-600 flex-1">{option}</span>
            <span className="text-xs text-slate-400 font-medium">
              ({counts[option] || 0})
            </span>
          </label>
        ))}
      </CollapsibleContent>
    </Collapsible>
  );
};

const FilterSidebar = ({
  open,
  onToggle,
  filterOptions,
  selectedProducts,
  selectedIndustries,
  selectedUseCaseTypes,
  selectedMarkets,
  onProductChange,
  onIndustryChange,
  onUseCaseTypeChange,
  onMarketChange,
  useCases
}) => {
  // Calculate counts for each filter option
  const counts = useMemo(() => {
    const productCounts = {};
    const industryCounts = {};
    const useCaseCounts = {};
    const marketCounts = {};

    useCases.forEach(uc => {
      uc.product.forEach(p => {
        productCounts[p] = (productCounts[p] || 0) + 1;
      });
      uc.industry.forEach(i => {
        industryCounts[i] = (industryCounts[i] || 0) + 1;
      });
      uc.useCase.forEach(t => {
        useCaseCounts[t] = (useCaseCounts[t] || 0) + 1;
      });
      uc.market.forEach(m => {
        marketCounts[m] = (marketCounts[m] || 0) + 1;
      });
    });

    return { productCounts, industryCounts, useCaseCounts, marketCounts };
  }, [useCases]);

  const hasFilters = selectedProducts.length > 0 || selectedIndustries.length > 0 || selectedUseCaseTypes.length > 0 || selectedMarkets.length > 0;

  return (
    <>
      {/* Mobile toggle button */}
      <button
        onClick={onToggle}
        className="lg:hidden fixed bottom-6 left-6 z-50 w-12 h-12 bg-[#FF6F40] text-white rounded-full shadow-lg flex items-center justify-center hover:bg-[#E65A2C] transition-colors"
        data-testid="filter-toggle-mobile"
      >
        <Filter className="w-5 h-5" />
      </button>

      {/* Sidebar */}
      <aside
        className={cn(
          "fixed lg:static inset-y-0 left-0 z-40 w-72 bg-white border-r border-slate-100 lg:border lg:rounded-2xl transition-transform duration-300 lg:transition-none",
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0 lg:hidden"
        )}
        data-testid="filter-sidebar"
      >
        <div className="h-full overflow-y-auto scrollbar-hide">
          {/* Mobile close button */}
          <div className="lg:hidden flex items-center justify-between p-4 border-b border-slate-100">
            <div className="flex items-center gap-2">
              <Filter className="w-5 h-5 text-slate-600" />
              <span className="font-semibold text-slate-700">Filters</span>
            </div>
            <button
              onClick={onToggle}
              className="w-8 h-8 rounded-full hover:bg-slate-100 flex items-center justify-center"
            >
              <X className="w-5 h-5 text-slate-500" />
            </button>
          </div>

          <div className="p-4 lg:p-5">
            {/* Desktop header */}
            <div className="hidden lg:flex items-center gap-2 mb-4">
              <Filter className="w-5 h-5 text-slate-600" />
              <span className="font-semibold text-slate-700 font-['Manrope']">Filters</span>
              {hasFilters && (
                <button
                  onClick={() => {
                    onProductChange([]);
                    onIndustryChange([]);
                    onUseCaseTypeChange([]);
                    onMarketChange([]);
                  }}
                  className="ml-auto text-xs text-[#FF6F40] hover:underline"
                  data-testid="clear-filters-btn"
                >
                  Clear all
                </button>
              )}
            </div>

            <FilterSection
              title="Product"
              options={filterOptions.products}
              selected={selectedProducts}
              onChange={onProductChange}
              counts={counts.productCounts}
            />

            <FilterSection
              title="Industry"
              options={filterOptions.industries}
              selected={selectedIndustries}
              onChange={onIndustryChange}
              counts={counts.industryCounts}
            />

            <FilterSection
              title="Use Case Type"
              options={filterOptions.useCases}
              selected={selectedUseCaseTypes}
              onChange={onUseCaseTypeChange}
              counts={counts.useCaseCounts}
            />

            <FilterSection
              title="Market"
              options={filterOptions.markets}
              selected={selectedMarkets}
              onChange={onMarketChange}
              counts={counts.marketCounts}
            />
          </div>
        </div>
      </aside>

      {/* Mobile backdrop */}
      {open && (
        <div
          className="fixed inset-0 bg-black/20 z-30 lg:hidden"
          onClick={onToggle}
        />
      )}
    </>
  );
};

export default FilterSidebar;
